<script setup lang="ts">
import { onMounted, toRefs } from 'vue';
import { LocationForm } from '@/components/Forms';
import { useOrganizationLocationStore, useOrganizationStore } from '@/store';
import { useToast } from 'primevue/usetoast';
import { LocationFormInterface } from '@/types';
import { can } from '@/composables';
import { PERMISSIONS } from '@/enum/permissions';

const toast = useToast();
const props = defineProps({
  organizationId: {
    type: Number,
    default: null,
  },
});

/**
 * Convert the properties into references.
 */
const { organizationId } = toRefs(props);

/**
 * All of the state references including the convertion of the
 * organization state object into a ref.
 */
const organizationLocationStore = useOrganizationLocationStore();
const organizationStore = useOrganizationStore();
/**
 * Initialize everything required to build out the component.
 */
onMounted(() => init());

/**
 * Initialize the organization location.
 */
const init = async () => {
  await organizationLocationStore.getLocation(organizationId.value);
};

const onSave = async (payload: LocationFormInterface) => {
  await organizationLocationStore.updateLocation(payload, organizationId.value);
  doToast(false);
};

/**
 * Display the toast message.
 * @param error:boolean
 */
const doToast = (error: boolean) => {
  toast.add({
    severity: error ? 'error' : 'success',
    summary: error ? 'Error' : 'Saved',
    detail: error ? 'There is an error with your form' : 'Organization Location information has been saved',
    life: 3000,
  });
};

/**
 * Handle the save and cancel event emitted by the child component
 * in order to reset the organization location data.
 */
const onReset = (data) => (organizationLocationStore.location = data);
</script>

<template>
  <LocationForm
    v-if="organizationStore.location"
    :permission-to-edit="can([PERMISSIONS.LOCATIONS_UPDATE])"
    :data="organizationStore.location"
    :is-organization="true"
    :loading="organizationStore.loading"
    @save="onSave"
    @cancel="onReset"
  />
</template>
